import { AlertRootScope, DialogProvider, RootBoundary } from "@fpsoftware/mvp";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createBrowserRouter,
  type RouteObject,
  RouterProvider,
} from "react-router-dom";
import { PageListBorrowers } from "@/borrower/PageListBorrowers";
import { PageIntegrateWebsite } from "@/integration/PageIntegrateWebsite";
import { PageListMortgages } from "@/mortgages/PageListMortgages";
import { PageMortgageDetail } from "@/mortgages/PageMortgageDetail";
import { PageMortgageExtract } from "@/mortgages/PageMortgageExtract";
import { PageMortgageNew } from "@/mortgages/PageMortgageNew";
import { PageSignUp } from "@/pages/PageSignUp";
import { PageListPurchases } from "@/purchases/PageListPurchases";
import { PagePurchaseDetail } from "@/purchases/PagePurchaseDetail";
import { PagePurchaseNew } from "@/purchases/PagePurchaseNew";
import { PageSettings } from "@/settings/PageSettings";
import { IframePageSubmit } from "@/submit/PageSubmit";
import { PageSubscription } from "@/subscription/PageSubscription";
import { PageSubscriptionSuccess } from "@/subscription/PageSubscriptionSuccess";
import { SubscriptionRoutes } from "@/subscription/SubscriptionRoutes";
import { AuthRoutes } from "./auth/AuthRoutes";
import { PageAuth } from "./auth/PageAuth";
import { PageAuthConsume } from "./auth/PageAuthConsume";
import { LinkGen } from "./LinkGen";
import { AppLayout } from "./layout/AppLayout";
import { PageNotFound } from "./layout/PageNotFound";
import "@fpsoftware/mvp/css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 mins
    },
  },
});

const routes: RouteObject[] = [
  {
    element: <RootBoundary />,
    children: [
      {
        path: LinkGen.Submit(":token"),
        element: <IframePageSubmit />,
      },
      {
        element: <AuthRoutes auth={false} />,
        children: [
          { path: LinkGen.Auth(), element: <PageAuth /> },
          {
            path: LinkGen.AuthConsume(":id", ":secret"),
            element: <PageAuthConsume />,
          },
          { path: LinkGen.SignUp(), element: <PageSignUp /> },
        ],
      },
      {
        element: <AuthRoutes auth={true} />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                path: LinkGen.Subscription(),
                element: <PageSubscription />,
              },
              {
                path: LinkGen.Subscription.Success(),
                element: <PageSubscriptionSuccess />,
              },
              {
                element: <SubscriptionRoutes />,
                children: [
                  { path: LinkGen.RFQ(), element: <PageListMortgages /> },
                  { path: LinkGen.RFQ.New(), element: <PageMortgageNew /> },
                  {
                    path: LinkGen.RFQ.Extract(),
                    element: <PageMortgageExtract />,
                  },
                  {
                    path: LinkGen.RFQ.Detail(":id"),
                    element: <PageMortgageDetail />,
                  },

                  { path: LinkGen.Purchase(), element: <PageListPurchases /> },
                  {
                    path: LinkGen.Purchase.New(),
                    element: <PagePurchaseNew />,
                  },
                  {
                    path: LinkGen.Purchase.Detail(":id"),
                    element: <PagePurchaseDetail />,
                  },

                  { path: LinkGen.Client(), element: <PageListBorrowers /> },

                  {
                    path: LinkGen.Integration(),
                    element: <PageIntegrateWebsite />,
                  },
                  { path: LinkGen.Settings(), element: <PageSettings /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <PageNotFound /> },
];

const router = createBrowserRouter(routes);

function App() {
  return (
    <AlertRootScope>
      <QueryClientProvider client={queryClient}>
        <DialogProvider>
          <RouterProvider router={router} />
        </DialogProvider>
      </QueryClientProvider>
    </AlertRootScope>
  );
}

export default App;
